import SideCartHeader from '@components/Layout/Header/SideShoppingCart/SideCartHeader';
import { useShoppingCart, useSideCart } from '@hooks/cart/useShoppingCart';
import {
  IAddressV2,
  ICustomerAddress,
  ISuggestedAddress,
} from '@monofront/types';
import Close from '@monofront/design-system/visuals/Icons/Old/Close';
import Button from '@monofront/design-system/components/Button';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import FreshAddressEligible from './FreshAddressEligible';
import FreshAddressForm from './FreshAddressForm';
import FreshHeader from './FreshHeader';
import FreshAddressNotEligible from './FreshAddressNotEligible';
import ModalFreshProducts from './ModalDeleteFreshProducts';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import logger from '@utils/logger';
import { useAuthentication } from '@context/businessContainer';
import { prismicService } from '@services/businessContainer';
import { checkAddressSource } from '@context/shoppingCart';
import {
  ELIGIBILITY_STATUS,
  productEligibilityDocument,
} from '@monofront/business/constants';
import { IPrismicProductEligibilityModal } from './types';
import { useEligibility } from '@monofront/business/contexts';
import useI18n from '@hooks/i18n/useI18n';

interface IFreshModalLayoutProps {
  freshStatus: ELIGIBILITY_STATUS;
  setFreshStatus: Dispatch<SetStateAction<ELIGIBILITY_STATUS>>;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

interface IFreshModalProps {
  source?: checkAddressSource;
  freshStatus: ELIGIBILITY_STATUS;
  setFreshStatus: Dispatch<SetStateAction<ELIGIBILITY_STATUS>>;
  address: ICustomerAddress | null;
  onSuggestedAddressSelect: (address: ISuggestedAddress | IAddressV2) => void;
  onClose: () => void;
}

const FreshModalLayout: React.FC<IFreshModalLayoutProps> = ({
  children,
  onClose,
  setFreshStatus,
  freshStatus,
  title,
}) => (
  <>
    <div
      className="page-header"
      data-testid="FreshCheckAddressModal-generic-layout"
    >
      <SideCartHeader onClose={onClose}>
        <FreshHeader
          setFreshStatus={setFreshStatus}
          freshStatus={freshStatus}
          title={title}
        />
      </SideCartHeader>
      <div className="mobile-only tw-flex tw-items-center tw-justify-between tw-p-4 tw-pb-2">
        <FreshHeader
          freshStatus={freshStatus}
          setFreshStatus={setFreshStatus}
          title={title}
        />
        <Button variant="clear" onClick={onClose}>
          <Close width={11} height={11} aria-hidden="true" />
        </Button>
      </div>
    </div>
    <div className=" tw-p-4">{children}</div>
  </>
);

const FreshModal: React.FC<IFreshModalProps> = ({
  source = checkAddressSource.DEFAULT,
  freshStatus,
  setFreshStatus,
  address,
  onSuggestedAddressSelect,
  onClose,
}) => {
  const [productEligibilityModalData, setProductEligibilityModalData] =
    useState<{ [key in checkAddressSource]?: IPrismicProductEligibilityModal }>(
      {},
    );
  const { locale } = useI18n();

  useEffect(() => {
    if (!productEligibilityModalData[source]) {
      prismicService
        .getDocumentByUid(...productEligibilityDocument[source], undefined, {
          ...(locale ? { lang: `${locale}-${locale}` } : {}),
        })
        .then((document) => {
          setProductEligibilityModalData((prev) => ({
            ...prev,
            [source]: document?.data,
          }));
        })
        .catch((e) => console.error(e));
    }
  }, [source]);

  switch (freshStatus) {
    case ELIGIBILITY_STATUS.ELIGIBLE_FOR_FRESH:
      return (
        <FreshModalLayout
          onClose={onClose}
          setFreshStatus={setFreshStatus}
          freshStatus={freshStatus}
          title={productEligibilityModalData[source]?.modal_title}
        >
          <FreshAddressEligible
            document={productEligibilityModalData[source]}
          />
        </FreshModalLayout>
      );
    case ELIGIBILITY_STATUS.NOTIFICATION_FORM:
      return (
        <FreshModalLayout
          onClose={onClose}
          setFreshStatus={setFreshStatus}
          freshStatus={freshStatus}
          title={productEligibilityModalData[source]?.modal_title}
        >
          <FreshAddressNotEligible
            setFreshStatus={setFreshStatus}
            hasNotificationForm
            hasRegisteredAddress={Boolean(address)}
            document={productEligibilityModalData[source]}
          />
        </FreshModalLayout>
      );
    case ELIGIBILITY_STATUS.NOT_ELIGIBLE:
      return (
        <FreshModalLayout
          onClose={onClose}
          setFreshStatus={setFreshStatus}
          freshStatus={freshStatus}
          title={productEligibilityModalData[source]?.modal_title}
        >
          <FreshAddressNotEligible
            setFreshStatus={setFreshStatus}
            hasRegisteredAddress={Boolean(address)}
            document={productEligibilityModalData[source]}
          />
        </FreshModalLayout>
      );
    default:
      return (
        <FreshModalLayout
          onClose={onClose}
          setFreshStatus={setFreshStatus}
          freshStatus={freshStatus}
          title={productEligibilityModalData[source]?.modal_title}
        >
          <FreshAddressForm
            onSuggestedAddressSelect={onSuggestedAddressSelect}
            document={productEligibilityModalData[source]}
          />
        </FreshModalLayout>
      );
  }
};

const FreshCheckAddress: React.FC = () => {
  const { sideCart, dispatchSideCart } = useSideCart();
  const { countryCode: marketCountryCode } = useI18n();
  const { customer } = useAuthentication();
  const {
    shoppingCart: { hasFreshProduct, hasUltraFreshProduct },
  } = useShoppingCart();
  const { address, checkEligibility } = useEligibility();
  const [freshStatus, setFreshStatus] = useState<ELIGIBILITY_STATUS>(
    ELIGIBILITY_STATUS.ADDRESS_FORM,
  );
  const [isModalOpen, setIsOpenModal] = useState(false);
  const tFresh = useTranslations('Fresh');

  const onSuggestedAddressSelect = async ({
    street,
    postcode,
    city,
    countryCode,
  }: ISuggestedAddress | IAddressV2) => {
    const country = countryCode ?? marketCountryCode;
    const address = {
      street,
      city,
      postalCode: postcode,
      country,
    };

    try {
      const { fresh: isFreshEligible, ultraFresh: isUltraFreshEligible } =
        checkEligibility(address);

      const isNotEligible = !isFreshEligible && !isUltraFreshEligible;

      if (isNotEligible) {
        /** display the not eligible step modal or the notification form */
        setFreshStatus(
          customer?.orderCount && customer?.orderCount > 0
            ? ELIGIBILITY_STATUS.NOT_ELIGIBLE
            : ELIGIBILITY_STATUS.NOTIFICATION_FORM,
        );

        return;
      }

      if (hasFreshProduct || hasUltraFreshProduct) {
        /** close the fresh/uf modal and display the cart with an alert */
        toast(tFresh('toastFreshSuccess'));
        dispatchSideCart({ type: 'viewMyCart' });

        return;
      }

      /** if the user is eligible and doesn't have any fresh or uf product to its cart, display the eligible step */
      setFreshStatus(ELIGIBILITY_STATUS.ELIGIBLE_FOR_FRESH);
    } catch (e) {
      logger.error('Error during check and update of user address (fresh)', {
        error: e,
      });
    }
  };

  const onClose = () => {
    hasFreshProduct &&
    [
      ELIGIBILITY_STATUS.NOTIFICATION_FORM,
      ELIGIBILITY_STATUS.NOT_ELIGIBLE,
    ].includes(freshStatus)
      ? setIsOpenModal(true)
      : dispatchSideCart({
          type: `${sideCart.openFromSideCart ? 'viewMyCart' : 'close'}`,
        });
  };

  return (
    <>
      <div className="fresh-container">
        <FreshModal
          onClose={onClose}
          freshStatus={freshStatus}
          setFreshStatus={setFreshStatus}
          address={address}
          onSuggestedAddressSelect={onSuggestedAddressSelect}
          source={sideCart.source}
        />
      </div>
      <ModalFreshProducts
        isOpen={isModalOpen}
        onClose={() => setIsOpenModal(false)}
      />
      <style jsx>{`
        .fresh-container {
          width: 100vw;
        }

        :global(.fresh-back-button) {
          margin-right: var(--space-3);
          height: 14px;
        }

        @media (min-width: 801px) {
          .fresh-container {
            width: 465px;
            max-width: 100%;
            background-color: var(--white);
            height: 100%;
            box-shadow: 0 2px 9px 0 rgba(0, 0, 0, 0.25);
            overflow-y: auto;
          }
        }
      `}</style>
    </>
  );
};

export default FreshCheckAddress;
